/**
 * Product analytics (PostHog).
 *
 * The app lives inside the GoHighLevel iframe, so persistence is
 * localStorage-only — third-party cookies are routinely blocked there. People
 * are identified by their GHL user id once SSO resolves; anonymous visitors
 * (the OAuth redirect, the landing page) do not create person profiles.
 *
 * Everything here is a no-op until `initAnalytics` has run with a key, so
 * tests and local builds without one send nothing.
 */

import axios from "axios";
import posthog from "posthog-js";

import env from "@/config";

/** The project's public ingestion token — safe to ship in the bundle. */
const DEFAULT_KEY = "phc_xsC3CHhLy3Z6YnattQpiD4YxpXNKbT42nbJAyPMpjq7Q";
const DEFAULT_HOST = "https://us.i.posthog.com";

export type AnalyticsApp = "wl" | "ct";

let enabled = false;

export function initAnalytics(): void {
  if (enabled || typeof window === "undefined" || import.meta.env.MODE === "test") return;
  const key = import.meta.env.VITE_POSTHOG_KEY ?? DEFAULT_KEY;
  if (!key) return;
  posthog.init(key, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || DEFAULT_HOST,
    ui_host: "https://us.posthog.com",
    defaults: "2025-05-24",
    person_profiles: "identified_only",
    persistence: "localStorage",
    capture_pageview: "history_change",
    session_recording: { maskAllInputs: true },
    // Uncaught errors and unhandled rejections, linked to the session replay.
    capture_exceptions: true,
  });
  enabled = true;
  installApiErrorTracking();
}

export interface AnalyticsIdentity {
  userId: string;
  companyId: string;
  locationId: string;
  isAgencyContext: boolean;
  name?: string;
  email?: string;
  role?: string;
  companyName?: string;
}

/** Tie this browser to the GHL user and tag every later event with its context. */
export function identifyUser(app: AnalyticsApp, who: AnalyticsIdentity): void {
  if (!enabled || !who.userId) return;
  const context = who.isAgencyContext ? "agency" : "subaccount";
  posthog.register({
    app,
    context,
    company_id: who.companyId || undefined,
    location_id: who.locationId || undefined,
  });
  posthog.identify(who.userId, {
    name: who.name || undefined,
    email: who.email || undefined,
    role: who.role || undefined,
    company_id: who.companyId || undefined,
    company_name: who.companyName || undefined,
    last_app: app,
    last_context: context,
  });
}

export function track(event: string, props?: Record<string, unknown>): void {
  if (!enabled) return;
  posthog.capture(event, props);
}

/** Report a crash caught by a React error boundary. */
export function captureException(error: unknown, props?: Record<string, unknown>): void {
  if (!enabled) return;
  posthog.captureException(error, props);
}

/*
 * Failed API calls.
 *
 * Most of what users report as "it doesn't work" is not a JavaScript crash but
 * a backend call that failed and showed a toast. Every failed request to this
 * app's own APIs is recorded as `api_error` with a normalised endpoint, so
 * failures group by route rather than by connection or location id. Request
 * and response bodies are never sent — they carry API keys and messages.
 */

const API_ORIGINS = [env.API_URL, env.ADMIN_API_URL].map(originOf).filter(Boolean);

function originOf(url: string): string {
  try {
    return new URL(url).origin;
  } catch {
    return "";
  }
}

const isIdSegment = (seg: string) =>
  /^\d+$/.test(seg) ||
  /^[0-9a-f]{24}$/i.test(seg) || // Mongo ObjectId
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg) ||
  (seg.length >= 16 && /\d/.test(seg) && /^[A-Za-z0-9_-]+$/.test(seg)); // CRM ids

/**
 * `https://api…/public/whatsapp/connections/665f…/qr?x=1` → `/public/whatsapp/connections/:id/qr`.
 * Null for any URL outside this app's APIs. Exported for tests.
 */
export function apiEndpoint(url: string | undefined, origins: string[] = API_ORIGINS): string | null {
  if (!url) return null;
  let parsed: URL;
  try {
    parsed = new URL(url, typeof window === "undefined" ? undefined : window.location.href);
  } catch {
    return null;
  }
  if (!origins.includes(parsed.origin)) return null;
  return parsed.pathname
    .split("/")
    .map((seg) => (isIdSegment(seg) ? ":id" : seg))
    .join("/");
}

function errorCode(body: unknown): string | undefined {
  if (!body || typeof body !== "object") return undefined;
  const b = body as Record<string, unknown>;
  const code = b.error ?? b.code ?? b.reason;
  return typeof code === "string" ? code.slice(0, 100) : undefined;
}

function errorMessage(body: unknown): string | undefined {
  if (!body || typeof body !== "object") return undefined;
  const msg = (body as Record<string, unknown>).message;
  return typeof msg === "string" ? msg.slice(0, 200) : undefined;
}

function reportApiError(
  endpoint: string,
  { method, status, body }: { method?: string; status: number; body?: unknown }
): void {
  track("api_error", {
    endpoint,
    method: (method || "GET").toUpperCase(),
    status,
    network_error: status === 0,
    error_code: errorCode(body),
    error_message: errorMessage(body),
  });
}

function installApiErrorTracking(): void {
  axios.interceptors.response.use(undefined, (err) => {
    if (!axios.isCancel(err)) {
      const endpoint = apiEndpoint(err?.config?.url);
      if (endpoint) {
        reportApiError(endpoint, {
          method: err?.config?.method,
          status: err?.response?.status ?? 0,
          body: err?.response?.data,
        });
      }
    }
    return Promise.reject(err);
  });

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const endpoint = apiEndpoint(url);
    if (!endpoint) return originalFetch(input, init);
    const method = init?.method || (input instanceof Request ? input.method : "GET");
    try {
      const res = await originalFetch(input, init);
      if (!res.ok) {
        const body = await res.clone().json().catch(() => undefined);
        reportApiError(endpoint, { method, status: res.status, body });
      }
      return res;
    } catch (err) {
      if ((err as { name?: string })?.name !== "AbortError") {
        reportApiError(endpoint, { method, status: 0 });
      }
      throw err;
    }
  };
}
